<template>
  <div>
    <div class="intro">
      <h2>WELCOME TO</h2>
      <h2>MEMBERS ONLY</h2>
    </div>

    <p>
      Fill in your details to create an account and enjoy early sale access,
      exclusive benefits plus much more.
    </p>

    <div class="countdown-block">
      <Countdown :end="endDate" theme="editorial" />
    </div>

    <SingleSignOn theme="primary-light" :return-url="returnUrl" />

    <div v-if="hasErrors" class="error-messages">
      <ul>
        <li v-for="error in errors" :key="error.code">
          {{ error.message }}
        </li>
      </ul>
    </div>

    <form class="form" @submit.prevent="handleFormSubmit">
      <TextField
        theme="white"
        :value="email"
        :validation="v$.email"
        autocomplete="email"
        type="email"
        placeholder="E-mail address"
        @input="(value) => (email = value)"
      />

      <TextField
        theme="white"
        :value="password"
        :validation="v$.password"
        autocomplete="new-password"
        type="password"
        placeholder="Password"
        @input="(value) => (password = value)"
      />

      <div class="field">
        <Button
          type="submit"
          width="full"
          :loading="loading"
          theme="primary-light"
        >
          Sign up with email
        </Button>
      </div>
    </form>

    <p class="footnote">
      By creating an account, you agree to receive promotional emails and
      exclusive offers from Rat & Boa.
    </p>

    <Button width="full" theme="outline-light" @click="$emit('login')">
      Already a member?
    </Button>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import SingleSignOn from "@/components/SingleSignOn.vue";
import TextField from "@/components/TextField.vue";
import Button from "@/components/ButtonV3.vue";
import Countdown from "@/components/landing/Countdown.vue";
import isEmpty from "lodash-es/isEmpty";
import { mapActions } from "vuex";

export default {
  name: "LandingRegisterForm",
  components: {
    SingleSignOn,
    TextField,
    Button,
    Countdown,
  },
  props: {
    endDate: {
      type: String,
      required: true,
    },
    returnUrl: {
      type: String,
      default: null,
    },
  },
  emits: ["success", "login"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      errors: [],
    };
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage("Email address is required", required),
        email,
      },
      password: {
        required: helpers.withMessage("Password is required", required),
      },
    };
  },
  computed: {
    hasErrors() {
      return this.errors.length > 0;
    },
  },
  methods: {
    ...mapActions("auth", ["attemptRegister"]),
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      const registerInput = {
        email: this.email,
        password: this.password,
      };

      this.loading = true;
      const data = await this.attemptRegister(registerInput);

      if (data.customerUserErrors.length >= 1) {
        this.errors = data.customerUserErrors;
        this.loading = false;
      } else {
        if (!isEmpty(data.customerAccessToken)) {
          this.$emit("success");
        }
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  gap: 20px;
  flex-direction: column;
}

:deep(.button--outline-light) {
  border-color: $color-white;
  color: $color-white;
}

h2 {
  @include editorial-headline(24px);
  text-transform: uppercase;
  margin: 0 auto 0;
}

.intro {
  margin-bottom: 16px;
}

p {
  @include editorial-body(14px);
  margin-bottom: 28px;
  margin: 0 auto 28px;
  max-width: 300px;
  @include respond-above(md) {
    max-width: auto;
  }
}

.divider {
  position: relative;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 28px;
  @include editorial-body(14px);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-bottom: solid 1px #d4d4d4;
    width: 40%;
    left: 0;
  }

  &::after {
    left: auto;
    right: 0;
  }
}

.sso-buttons {
  border-bottom: none;
  margin: 0 0 28px;
  padding: 0;
}

.countdown-block {
  margin-bottom: 28px;
}

.footnote {
  margin-top: 28px;
  @include utility-label(13px);
  text-align: center;
}

.error-messages {
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: $color-red;
  padding: 16px;
  color: $color-white;
  border-radius: 2px;
  font-size: 10px;
}

p {
  @include editorial-body(14px);
  margin-bottom: 28px;
}
</style>
